<template>
  <!-- The web admin's bell for ຖອນເງິນ COD ລ່ວງໜ້າ (Finance, 2026-10-26): a shop
       that asks for an advance in the app shows up here, so Finance comes to
       transfer it. Polled — a page, never a money decision. -->
  <vs-button class="cod-alert-bell" flat icon :color="openCount ? 'warn' : 'dark'"
             :title="openCount ? 'ລໍຖ້າໂອນເງິນ COD ລ່ວງໜ້າ ' + openCount + ' ລາຍການ' : 'ບໍ່ມີລາຍການລໍຖ້າໂອນ'"
             aria-label="ລາຍການລໍຖ້າໂອນເງິນ COD ລ່ວງໜ້າ" @click="openQueue">
    <i class="fal fa-bell"></i>
    <span class="badge" v-if="openCount">{{ openCount > 99 ? '99+' : openCount }}</span>
  </vs-button>
</template>

<script>
import {fetchTransferAlerts, formatKip} from '@/services/codAdvanceService';

// The newest request this browser already announced: per viewer, a convenience
// only — unreadable storage just means the next poll starts quietly.
const SEEN_KEY = 'cod_advance_alert_last_id';
// CodAdvanceTransfers listens for it.
export const QUEUE_REFRESH_EVENT = 'cod-advance-queue-refresh';
const POLL_MS = 60 * 1000;

// Vuesax writes a notification's text with innerHTML: a shop name is customer
// text and must never reach it as markup.
const escapeHtml = (value) => String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export default {
  name: 'CodAdvanceAlertBell',
  data() {
    return {openCount: 0, lastSeen: null, timer: null, stopped: false, polling: false};
  },
  created() {
    this.lastSeen = this.readSeen();
    this.poll();
    this.timer = setInterval(() => this.poll(), POLL_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  },
  methods: {
    // null = nothing remembered yet; 0 = the queue was empty when last seen.
    readSeen() {
      try {
        const raw = localStorage.getItem(SEEN_KEY);
        const value = Number(raw);
        return raw !== null && raw !== '' && value >= 0 ? value : null;
      } catch (_) {
        return null;
      }
    },
    writeSeen(id) {
      this.lastSeen = id;
      try {
        localStorage.setItem(SEEN_KEY, String(id));
      } catch (_) {
        // A private window: the bell still counts, it just re-announces after a reload.
      }
    },
    // A hidden tab does not poll; coming back polls at once.
    onVisibility() {
      if (document.visibilityState === 'visible') this.poll();
    },
    async poll() {
      // One request at a time: the interval and a tab coming back must not both
      // announce the same requests, nor an older answer overwrite a newer count.
      if (this.stopped || this.polling || document.visibilityState === 'hidden') return;
      this.polling = true;
      try {
        const data = await fetchTransferAlerts(this.lastSeen);
        this.openCount = Number(data.open_count) || 0;
        const latest = Number(data.latest_id) || 0;
        // First poll in this browser: remember where the queue stands, announce nothing.
        if (this.lastSeen !== null && Number(data.new_count) > 0) {
          this.announce(Number(data.new_count), data.items || []);
        }
        if (this.lastSeen === null || latest > this.lastSeen) {
          this.writeSeen(latest);
        }
      } catch (e) {
        // Not a queue role (the server says 403): stop asking. Anything else: try next time.
        if (e && e.response && e.response.status === 403) {
          this.stopped = true;
          clearInterval(this.timer);
        }
      } finally {
        this.polling = false;
      }
    },
    // A few named, the rest counted: a tab left hidden may come back to dozens.
    announce(count, items) {
      const shown = items.slice(0, 3);
      shown.forEach((item) => {
        this.$vs.notification({
          duration: 8000,
          color: 'warn',
          position: 'top-right',
          title: 'ຮ້ານຂໍຖອນເງິນ COD ລ່ວງໜ້າ',
          text: escapeHtml((item.shop_name || '—') + ' · ' + formatKip(item.total_price) + ' · ' + (item.invoice_number || '')),
          onClick: () => this.openQueue(),
        });
      });
      if (count > shown.length) {
        this.$vs.notification({
          duration: 8000,
          color: 'warn',
          position: 'top-right',
          title: 'ຄຳຂໍຖອນເງິນ COD ລ່ວງໜ້າໃໝ່',
          text: 'ອີກ ' + (count - shown.length) + ' ລາຍການ ລໍຖ້າໂອນ',
          onClick: () => this.openQueue(),
        });
      }
    },
    // Already on the queue page: ask it to show the waiting tab and reload, or
    // the click would do nothing and the announced request would not appear.
    openQueue() {
      if (this.$route.name === 'cod.advance.transfers') {
        window.dispatchEvent(new CustomEvent(QUEUE_REFRESH_EVENT));
        return;
      }
      this.$router.push({name: 'cod.advance.transfers'}).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.cod-alert-bell {
  position: relative;
  margin-right: 6px;

  .badge {
    position: absolute;
    top: -4px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #e5484d;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
  }
}
</style>
